// Dashboard user preferences (range / role / metric / sort selections) persisted as a single
// JSON object in localStorage under one key, shared across all sections via context so the
// sections don't clobber each other's slice of the object.
import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

const STORAGE_KEY = 'Dashboard.prefs';

type Prefs = Record<string, unknown>;

interface DashboardPrefsContextValue {
  prefs: Prefs;
  setPref: (key: string, value: unknown) => void;
}

const DashboardPrefsContext = createContext<DashboardPrefsContextValue>({ prefs: {}, setPref: () => {} });

const readPrefs = (): Prefs => {
  try {
    const item = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    return item ? (JSON.parse(item) as Prefs) : {};
  } catch {
    return {};
  }
};

export const DashboardPrefsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [prefs, setPrefs] = useState<Prefs>(readPrefs);

  const setPref = useCallback((key: string, value: unknown) => {
    setPrefs(prev => {
      const next = { ...prev, [key]: value };
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / serialization errors */
      }
      return next;
    });
  }, []);

  return <DashboardPrefsContext.Provider value={{ prefs, setPref }}>{children}</DashboardPrefsContext.Provider>;
};

// useState-like API backed by the shared prefs object. Returns the stored value (or the default)
// and a setter that persists into the single JSON blob.
export function useDashboardPref<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const { prefs, setPref } = useContext(DashboardPrefsContext);
  const value = (prefs[key] !== undefined ? prefs[key] : defaultValue) as T;
  const setValue = useCallback((v: T) => setPref(key, v), [key, setPref]);
  return [value, setValue];
}